"use client";

import DashboardBanner from "@components/Banner/DashboardBanner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@tracify/ui/tabs";
import { useParams, useRouter, useSearchParams } from "next/navigation";
import AssignAdAccountsCard from "@components/AdAccounts/AssignAdAccountsCard";
import { GoogleConversionPostbackCard } from "@components/AdAccounts/GoogleConversionPostbackCard";
import { MetaConversionPostbackCard } from "@components/AdAccounts/MetaConversionPostbackCard";
import { OutbrainConversionPostbackCard } from "@components/AdAccounts/OutbrainConversionPostbackCard";
import ReconnectAdAccountsCard from "@components/AdAccounts/ReconnectAdAccountsCard";
import { queryParamsToPurge } from "@components/LinkWithQuery/LinkWithQuery";
import { useConnectedAdAccounts } from "@lib/api-hooks/useAdAccounts/useConnectedAdAccounts";
import useSelectedStores from "@lib/hooks/use-selected-stores";
import { platformConfigs } from "./tracking-config";
import { TaboolaConnectionProvider } from "./TaboolaConnectionContext";
import TrackingParameterSection from "./TrackingParameterSection";
import { DecifySettings } from "./DecifySettings";
import { useFeatureFlag } from "@lib/hooks/useFeatureFlags";
import { SidebarBadge } from "@tracify/ui/sidebar-badge";
import {
  buildPathWithSearchParams,
  cloneSearchParams,
  deleteSearchParams,
} from "@lib/navigation/url-search-params";

type Props = {
  initialTab?: string;
};

export type Integration = {
  name: string;
  img: string;
  buttonText: string;
  disabled: boolean;
};

export const integrations: Record<string, Integration> = {
  google: {
    name: "Google Ads",
    img: "/static/logos/google-logo.webp",
    buttonText: "Connect Google Ads Account",
    disabled: false,
  },
  fb: {
    name: "Meta",
    img: "/static/logos/meta-logo.png",
    buttonText: "Connect Meta Account",
    disabled: false,
  },
  facebook: {
    name: "Meta",
    img: "/static/logos/meta-logo.png",
    buttonText: "Connect Meta Account",
    disabled: false,
  },
  tiktok: {
    name: "TikTok",
    img: "/static/logos/tiktok-logo.png",
    buttonText: "Connect TikTok Account",
    disabled: false,
  },
  pinterest: {
    name: "Pinterest",
    img: "/static/logos/pinterest-logo.png",
    buttonText: "Connect Pinterest Account",
    disabled: false,
  },
  outbrain: {
    name: "Outbrain",
    img: "/static/logos/outbrain-logo.svg",
    buttonText: "Connect Outbrain Account",
    disabled: false,
  },
  msads: {
    name: "Microsoft Ads",
    img: "/static/logos/microsoft-logo.svg",
    buttonText: "Connect Microsoft Ads Account",
    disabled: false,
  },
  taboola: {
    name: "Taboola",
    img: "/static/logos/taboola-logo.svg",
    buttonText: "Connect Taboola Account",
    disabled: false,
  },
};

export function CombinedIntegrationsDashboard({ initialTab }: Props = {}) {
  const router = useRouter();
  const type = useParams<{ type?: string }>()?.type;
  const searchParams = useSearchParams();
  const isDecifyPpsEnabled = useFeatureFlag("decify_pps");
  const { selectedOrganisation } = useSelectedStores();
  const { data: connectedAdAccounts } = useConnectedAdAccounts({
    organisationId: selectedOrganisation ?? "",
    includeAll: false,
  });

  const currentTab = type || initialTab || "meta";

  const handleTabChange = (value: string) => {
    const params = deleteSearchParams(
      cloneSearchParams(searchParams),
      queryParamsToPurge
    );

    router.push(
      buildPathWithSearchParams(`/settings/integrations/${value}`, params)
    );
  };

  const connectedDot = (
    <div className="h-1.5 w-1.5 bg-green-500 rounded-full" />
  );
  const disconnectedDot = (
    <div className="h-1.5 w-1.5 bg-zinc-500 rounded-full" />
  );

  // Channels with ad account connection capability
  const connectableChannels: Array<{
    id: string;
    name: string;
    type:
      | "fb"
      | "google"
      | "tiktok"
      | "pinterest"
      | "outbrain"
      | "msads"
      | "taboola";
    accountsKey:
      | "facebook"
      | "google"
      | "tiktok"
      | "pinterest"
      | "outbrain"
      | "msads"
      | "taboola";
  }> = [
    {
      id: "meta",
      name: "Meta",
      type: "fb",
      accountsKey: "facebook",
    },
    {
      id: "google",
      name: "Google Ads",
      type: "google",
      accountsKey: "google",
    },
    {
      id: "tiktok",
      name: "TikTok",
      type: "tiktok",
      accountsKey: "tiktok",
    },
    {
      id: "pinterest",
      name: "Pinterest",
      type: "pinterest",
      accountsKey: "pinterest",
    },
    {
      id: "outbrain",
      name: "Outbrain",
      type: "outbrain",
      accountsKey: "outbrain",
    },
    {
      id: "msads",
      name: "Microsoft Ads",
      type: "msads",
      accountsKey: "msads",
    },
    {
      id: "taboola",
      name: "Taboola",
      type: "taboola",
      accountsKey: "taboola",
    },
  ];

  // Channels without ad account connection (tracking parameters only)
  const trackingOnlyChannels = [
    { id: "email", name: "Email" },
    { id: "whatsapp", name: "Whatsapp" },
    { id: "snapchat", name: "Snapchat" },
    { id: "criteo", name: "Criteo" },
  ];

  // Find the tracking config for a given channel
  const getTrackingConfig = (channelId: string) => {
    const normalizedId =
      channelId === "msads"
        ? "microsoft"
        : channelId === "google"
          ? "google"
          : channelId;
    return platformConfigs.find(
      (config) =>
        config.integrationId.toLowerCase() === normalizedId.toLowerCase() ||
        config.name.toLowerCase() === channelId.toLowerCase()
    );
  };

  return (
    <TaboolaConnectionProvider>
      <div className="relative">
        <div className="lg:sticky lg:top-0 bg-background pt-4 pb-2 z-navbar">
          <DashboardBanner />
          <h2 className="h2">Integrations</h2>
        </div>
        <div>
          <Tabs value={currentTab}>
            <TabsList
              variant="alternate"
              className="flex w-full 2xl:w-fit mx-auto overflow-x-auto justify-start 2xl:justify-center scrollbar-thin scrollbar-thumb-muted scrollbar-track-transparent flex-nowrap"
            >
              {/* Connectable channels */}
              {connectableChannels.map((channel) => (
                <TabsTrigger
                  key={channel.id}
                  value={channel.id}
                  onClick={() => handleTabChange(channel.id)}
                  variant="alternate"
                  className="whitespace-nowrap shrink-0"
                >
                  <span className="flex items-center gap-2">
                    {connectedAdAccounts?.accounts?.[channel.accountsKey]
                      ?.length
                      ? connectedDot
                      : disconnectedDot}
                    {channel.name}
                  </span>
                </TabsTrigger>
              ))}

              {/* Tracking-only channels */}
              {trackingOnlyChannels.map((channel) => (
                <TabsTrigger
                  key={channel.id}
                  value={channel.id}
                  onClick={() => handleTabChange(channel.id)}
                  variant="alternate"
                  className="whitespace-nowrap shrink-0"
                >
                  <span className="flex items-center gap-2">
                    {channel.name}
                  </span>
                </TabsTrigger>
              ))}

              {/* Decify */}
              {isDecifyPpsEnabled && (
                <TabsTrigger
                  value="decify"
                  onClick={() => handleTabChange("decify")}
                  variant="alternate"
                  className="whitespace-nowrap shrink-0"
                >
                  Decify
                  <SidebarBadge variant="warning" className="ml-1.5">
                    Beta
                  </SidebarBadge>
                </TabsTrigger>
              )}
            </TabsList>

            {/* Tab content for connectable channels */}
            {connectableChannels.map((channel) => {
              const trackingConfig = getTrackingConfig(channel.id);
              const hasConnectedAdAccount = Boolean(
                connectedAdAccounts?.accounts?.[channel.accountsKey]?.length
              );

              return (
                <TabsContent key={channel.id} value={channel.id}>
                  <div className="space-y-4">
                    {/* Tracking Parameters Section */}
                    {trackingConfig && (
                      <TrackingParameterSection config={trackingConfig} />
                    )}

                    {/* Ad Account Connection Section */}
                    {hasConnectedAdAccount ? (
                      <>
                        {channel.id === "meta" ? (
                          <MetaConversionPostbackCard />
                        ) : null}
                        {channel.id === "google" ? (
                          <GoogleConversionPostbackCard />
                        ) : null}
                        {channel.id === "outbrain" ? (
                          <OutbrainConversionPostbackCard />
                        ) : null}
                      </>
                    ) : null}
                    <ReconnectAdAccountsCard type={channel.accountsKey} />
                    <AssignAdAccountsCard type={channel.accountsKey} />
                  </div>
                </TabsContent>
              );
            })}

            {/* Tab content for tracking-only channels */}
            {trackingOnlyChannels.map((channel) => {
              const trackingConfig = getTrackingConfig(channel.id);

              return (
                <TabsContent key={channel.id} value={channel.id}>
                  {trackingConfig && (
                    <TrackingParameterSection config={trackingConfig} />
                  )}
                </TabsContent>
              );
            })}
            {/* Decify tab content */}
            {isDecifyPpsEnabled && (
              <TabsContent value="decify">
                <DecifySettings />
              </TabsContent>
            )}
          </Tabs>
        </div>
      </div>
    </TaboolaConnectionProvider>
  );
}
